import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import PlaybookCard from './PlaybookCard';
import { getAuth } from 'firebase/auth';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';

export default function Playbooks() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [refreshingPlaybook, setRefreshingPlaybook] = useState(null);

  useEffect(() => {
    async function fetchPlaybooks() {
      if (!user) return;
      
      setLoading(true);
      try {
        const userPlaybooksRef = collection(db, 'user_playbooks');
        const q = query(userPlaybooksRef, where('user_id', '==', user.uid));
        const querySnapshot = await getDocs(q);
        
        const fetchedPlaybooks = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedPlaybooks.push({ id: doc.id, ...data });
        });
        
        setPlaybooks(fetchedPlaybooks);
      } catch (error) {
        console.error('Error fetching playbooks:', error);
      }
      setLoading(false);
    }

    fetchPlaybooks();
  }, [user]);

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const handleViewPlaybook = (playbookId) => {
    navigate(`/playbooks/${playbookId}`);
  };

  const handleDeletePlaybook = async (playbookId) => {
    if (deleteConfirm === playbookId) {
      try {
        // Get the current user
        if (!user) {
          return;
        }

        // Get the playbook data
        const playbook = playbooks.find(p => p.id === playbookId);

        // Verify ownership
        if (!playbook) {
          return;
        }

        // Get the document reference and verify it exists
        const playbookRef = doc(db, 'user_playbooks', playbookId);
        const playbookDoc = await getDoc(playbookRef);
        
        if (!playbookDoc.exists()) {
          return;
        }

        const playbookData = playbookDoc.data();

        // Verify the user owns this playbook
        if (playbookData.user_id !== user.uid) {
          return;
        }

        // Delete the document
        await deleteDoc(playbookRef);
        setPlaybooks(playbooks.filter(p => p.id !== playbookId));
      } catch (error) {
        console.error('Error deleting playbook:', error);
        // Log additional error details
        if (error.code) {
          console.error('Error code:', error.code);
        }
        if (error.message) {
          console.error('Error message:', error.message);
        }
      }
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(playbookId);
    }
  };

  const handleRefreshPlaybook = async (playbookId) => {
    try {
      setRefreshingPlaybook(playbookId);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        // Prompt user to sign in
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true); // force refresh
      const playbook = playbooks.find(p => p.id === playbookId);
      if (!playbook) {
        return;
      }
      let portfolioToSend = playbook.portfolio;
      const response = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portfolio: portfolioToSend
        })
      });
      if (!response.ok) {
        throw new Error('Failed to refresh playbook');
      }
      const data = await response.json();
      // Update the playbook with new data
      const playbookRef = doc(db, 'user_playbooks', playbookId);
      await updateDoc(playbookRef, {
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => ({
          pick_name: pick.pick,
          opponent_name: pick.opponent,
          budget_amount: pick.augmented_weight,
          american_odds: pick.odds_us,
          decimal_odds: parseFloat(pick.odds),
          supporting_strategies: pick.strategy?.length || 0,
          type: pick.pick_type.toLowerCase()
        })),
      });
      setPlaybooks(playbooks.map(p => {
        if (p.id === playbookId) {
          return {
            ...p,
            last_updated: new Date().toISOString(),
            event_name: data.event_name,
            event_date: data.event_date,
            picks: data.picks.map(pick => ({
              pick_name: pick.pick,
              opponent_name: pick.opponent,
              budget_amount: pick.augmented_weight,
              american_odds: pick.odds_us,
              decimal_odds: parseFloat(pick.odds),
              supporting_strategies: pick.strategy?.length || 0,
              type: pick.pick_type.toLowerCase()
            })),
          };
        }
        return p;
      }));
    } catch (error) {
      console.error('Error refreshing playbook:', error);
    } finally {
      setRefreshingPlaybook(null);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <header className="page-header">
            <div className="header-content">
              <h1>Your Playbooks</h1>
              <p className="header-subtitle">Build and manage your custom betting strategies</p>
            </div>
            <div className="header-stats">
              <div className="stat-card">
                <span className="stat-value">-</span>
                <span className="stat-label">Total Playbooks</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">-</span>
                <span className="stat-label">Active Events</span>
              </div>
            </div>
          </header>
          <div className="content-section loading-section">
            <div className="loading">
              <div className="loading-spinner"></div>
              <span>Loading your playbooks...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeEvents = [...new Set(playbooks.map(p => p.event?.name).filter(Boolean))];
  const totalReturns = playbooks.reduce((sum, p) => sum + (p.portfolio_returns || 0), 0);
  const averageReturn = playbooks.length ? (totalReturns / playbooks.length) : 0;

  return (
    <>
      <style jsx>{`
        .page-wrapper {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--background);
          padding: 16px;
        }

        .page-container {
          flex: 1;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .page-header {
          background: linear-gradient(135deg, rgba(123, 73, 209, 0.08) 0%, rgba(0, 0, 0, 0) 100%);
          border-radius: 20px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          border: 1px solid rgba(123, 73, 209, 0.15);
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
        }

        .header-content {
          text-align: center;
        }

        .header-content h1 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0.8));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -0.02em;
        }

        .header-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          margin: 8px 0 0 0;
          line-height: 1.5;
        }

        .header-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          width: 100%;
        }

        .stat-card {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          border-color: rgba(123, 73, 209, 0.3);
          background: rgba(123, 73, 209, 0.05);
        }

        .stat-card .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 600;
          color: var(--primary);
          margin-bottom: 4px;
        }

        .stat-card .stat-label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .create-button-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .create-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 28px;
          width: 100%;
          max-width: 300px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(123, 73, 209, 0.2);
        }

        .create-button:disabled {
          background: rgba(123, 73, 209, 0.3);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .create-button:disabled:hover {
          background: rgba(123, 73, 209, 0.3);
          transform: none;
          box-shadow: none;
        }

        .tooltip {
          position: relative;
          display: inline-block;
        }

        .tooltip .tooltip-text {
          visibility: hidden;
          width: 200px;
          background-color: rgba(0, 0, 0, 0.9);
          color: #fff;
          text-align: center;
          border-radius: 6px;
          padding: 8px;
          position: absolute;
          z-index: 1;
          bottom: 125%;
          left: 50%;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 0.2s;
          font-size: 13px;
          font-weight: normal;
          pointer-events: none;
        }

        .tooltip:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }

        .playbooks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: rgba(255, 255, 255, 0.6);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          border: 1px solid rgba(123, 73, 209, 0.15);
          border-radius: 20px;
          background: linear-gradient(135deg, rgba(123, 73, 209, 0.03) 0%, rgba(0, 0, 0, 0) 100%);
        }

        .empty-state p {
          font-size: 15px;
          margin: 0;
          max-width: 300px;
          line-height: 1.5;
        }

        @media (min-width: 640px) {
          .page-wrapper {
            padding: 24px;
          }

          .page-header {
            padding: 32px;
          }

          .header-content {
            text-align: left;
          }

          .header-content h1 {
            font-size: 36px;
          }

          .header-subtitle {
            font-size: 16px;
          }

          .create-button-wrapper {
            justify-content: flex-end;
          }

          .create-button {
            width: auto;
          }
        }

        @media (min-width: 1024px) {
          .page-wrapper {
            padding: 32px;
          }

          .page-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 32px;
            gap: 32px;
          }

          .header-content {
            text-align: left;
            flex: 1;
          }

          .header-stats {
            display: flex;
            width: auto;
            margin-left: auto;
            gap: 16px;
          }

          .stat-card {
            min-width: 160px;
          }
        }
      `}</style>

      <div className="page-wrapper">
        <div className="page-container">
          <header className="page-header">
            <div className="header-content">
              <h1>Your Playbooks</h1>
              <p className="header-subtitle">Build and manage your custom betting strategies</p>
            </div>
            <div className="header-stats">
              <div className="stat-card">
                <span className="stat-value">{playbooks.length}/{userProfile?.settings?.available_playbooks || 5}</span>
                <span className="stat-label">Total Playbooks</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{activeEvents.length}</span>
                <span className="stat-label">Active Events</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{formatPercent(averageReturn)}</span>
                <span className="stat-label">Avg. Return</span>
              </div>
            </div>
          </header>

          {playbooks.length > 0 && (
            <div className="create-button-wrapper">
              <div className="tooltip">
                <button 
                  className="create-button" 
                  onClick={() => navigate('/playbooks/build')}
                  disabled={playbooks.length >= (userProfile?.settings?.available_playbooks || 5)}
                >
                  <AddIcon /> Create New Playbook
                </button>
                {playbooks.length >= (userProfile?.settings?.available_playbooks || 5) && (
                  <span className="tooltip-text">
                    You have reached your maximum number of available playbooks
                  </span>
                )}
              </div>
            </div>
          )}

          {playbooks.length === 0 ? (
            <div className="empty-state">
              <MenuBookIcon sx={{ fontSize: 48, opacity: 0.6 }} />
              <p>You haven't created any playbooks yet. Start by creating your first playbook!</p>
              <button className="create-button" onClick={() => navigate('/playbooks/build')}>
                <AddIcon /> Create New Playbook
              </button>
            </div>
          ) : (
            <div className="playbooks-grid">
              {playbooks.map((playbook) => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  isActive={false}
                  onClick={() => {}}
                  onView={handleViewPlaybook}
                  onDelete={handleDeletePlaybook}
                  onRefresh={handleRefreshPlaybook}
                  deleteConfirm={deleteConfirm}
                  showActions={true}
                  isRefreshing={refreshingPlaybook === playbook.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
} 